<template>
  <div>
    <AuBreadcrumb
      :parent-name="$t('router.pages.dashboard')"
    />
    <RouterView v-if="isCompanyManager" />
    <Form
      v-else
      :is-bordered="false"
    >
      <template #title>
        {{ $t('companies.label') }}
      </template>
      <template #subtitle>
        <div class="items-count">
          <span>
            {{ $t("companies.companiesCount") }}:
          </span>
          <AuSkeleton v-if="loading" />
          <span v-else>
            {{ companyCount }}
          </span>
        </div>
      </template>

      <template #header-right>
        <div class="btn_indent">
          <AuButton
            bordered
            center
            type="primary"
            width="125px"
            @click="showCompanyManager(null)"
          >
            <span class="btn-text">
              {{ $t('companies.addCompanyButton') }}
            </span>
          </AuButton>
        </div>
      </template>

      <!-- Заменяем AuTable на UTable - только Edit в меню -->
      <UTable
        class="companies-table"
        :class="{ 'briefcase-table-hiddable': true }"
        :items="items"
        :columns="tableColumns"
        :loading="loading"
      >
        <!-- Слот для названия компании -->
        <template #name="{ value }">
          {{ value }}
        </template>

        <!-- Слот для страны -->
        <template #country="{ value }">
          {{ value }}
        </template>

        <!-- Слот для описания -->
        <template #description="{ value }">
          {{ value }}
        </template>

        <!-- Слот для статуса верификации -->
        <template #isVerifyRequest="{ value }">
          {{ value }}
        </template>

        <!-- Слот для действий - пустой, так как используется type="menu" -->
        <template #actions>
          <!-- Пустой слот, рендеринг через menuFunction -->
        </template>
      </UTable>

      <!-- Аккордеон для мобильной версии (здесь есть и Edit, и Delete) -->
      <div class="companies-accordion">
        <AuAccordion
          v-for="(company, index) in items"
          :key="company.id"
          :last-child="(index == items.length - 1)"
        >
          <template #title>
            <span class="company-header">{{ company.name }}</span>
          </template>
          <template #content>
            <div class="company-info container-fluid">
              <div class="row">
                <div class="col info-title">
                  <span>{{ $t('companies.secondColumn') }}</span>
                </div>
                <div class="col">
                  <span>{{ company.country }}</span>
                </div>
              </div>
              <div class="row">
                <div class="col info-title">
                  <span>Верификация</span>
                </div>
                <div class="col">
                  <span>{{ company.isVerified }}</span>
                </div>
              </div>
              <div class="row">
                <div class="col info-title">
                  <span>{{ $t('companies.fourthColumn') }}</span>
                </div>
                <div class="col">
                  <span>{{ company.isVerifyRequest }}</span>
                </div>
              </div>
              <div class="row">
                <div class="col buttons">
                  <AuButton
                    width="100%"
                    center
                    @click="showCompanyManager(company.actionData.index)"
                  >
                    {{ $t("counterparties.editAction") }}
                  </AuButton>
                  <!-- Кнопка Delete есть только в мобильной версии -->
                  <AuButton
                    width="100%"
                    center
                    @click="showDeleteDialog(company.actionData.index)"
                  >
                    {{ $t("counterparties.deleteAction") }}
                  </AuButton>
                </div>
              </div>
            </div>
          </template>
        </AuAccordion>
      </div>

      <AuPagination
        v-if="companyCount > itemsPerPage"
        :hide="loading"
        :total-items="companyCount"
        @pageChanged="getCompaniesData"
      />
    </Form>

    <!-- Модальное окно подтверждения удаления -->
    <AuModal
      v-model="deleteDialog"
      width="500px"
    >
      <template #header>
        <span class="modal_head_text">
          {{ $t('companies.deleteDialog.title') }}
        </span>
      </template>
      <template #body>
        <span>
          {{ $t('companies.deleteDialog.subtitle') }} - {{ deleteName }}?
        </span>
      </template>
      <template #footer>
        <div class="modal_button">
          <div class="modal_button_margin">
            <AuButton
              bordered
              center
              width="80px"
              @click="del"
            >
              {{ $t('companies.deleteDialog.agree') }}
            </AuButton>
          </div>
          <div class="modal_button_margin">
            <AuButton
              bordered
              center
              width="80px"
              @click="deleteDialog=false"
            >
              {{ $t('companies.deleteDialog.cancel') }}
            </AuButton>
          </div>
        </div>
      </template>
    </AuModal>
  </div>
</template>

<script>
import _ from "lodash";
import i18n from "@/localization/localization.js";
import isUserRole from "@/help/isUserRole";
import UTable from "@/components/uikit/uComponents/UTable.vue";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    UTable
  },
  data() {
    return {
      deleteDialog: false,
      deleteId: null,
      deleteIndex: null,
      deleteName: null,
      loading: true,
      itemsPerPage: 30
    };
  },

  computed: {
    ...mapGetters({
      companies: "companies",
      country: "country",
      companyCount: "companyCount"
    }),

    isCompanyManager() {
      return _.includes(this.$route.path, "company-manager");
    },

    // Computed колонки для UTable
    tableColumns() {
      return [
        {
          property: "name",
          header: this.$t("companies.firstColumn"),
          align: "start"
        },
        {
          property: "country",
          header: this.$t("companies.secondColumn"),
          align: "center"
        },
        {
          property: "description",
          header: this.$t("companies.thirdColumn"),
          align: "center"
        },
        {
          property: "isVerifyRequest",
          header: this.$t("companies.fourthColumn"),
          align: "center"
        },
        {
          property: "actions",
          header: this.$t("companies.fifthColumn"),
          align: "center",
          type: "menu",
          // Только Edit в меню, как в action-type="company"
          menuFunction: this.getCompanyMenuItems
        }
      ];
    },

    // Форматирование данных для таблицы
    items() {
      return _.map(this.companies, (company, index) => {
        // Создаем уникальный ID
        const companyId = company.id || `company-${index}-${Date.now()}`;

        return {
          id: companyId,
          name: i18n.global.locale == "ru" ? company.nameRus : company.nameEng,
          country: _.chain(this.countryOptions).find({ value: company.countryCode }).get("display").value(),
          description: i18n.global.locale == "ru" ? company.descriptionRus : company.descriptionEng,
          isVerified: company.isVerified ? this.$t("companies.companyVerified") : this.$t("companies.companyNotVerified"),
          isVerifyRequest: company.isVerified ? this.$t("companies.companyVerified") :
            (company.isVerifyRequest ? this.$t("companies.companyNotVerified") :
              this.$t("companies.companyIsDraft")),
          // Данные для контекстного меню
          actionData: {
            id: company.id,
            index: index,
            isVerifyRequest: company.isVerifyRequest,
            name: company.nameRus
          }
        };
      });
    },

    countryOptions() {
      return _.map(this.country, c => ({ display: c.name, value: c.code }));
    }
  },

  async mounted() {
    if (isUserRole() !== "issuer") {
      this.$router.push({ name: "base" });
    }
    if (_.isEmpty(this.country)) {
      await this.getCountry();
    }
    if (_.isEmpty(this.companies)) {
      await this.getCompaniesData(1);
    }

    this.loading = false;
  },

  methods: {
    ...mapActions({
      getHydraCompanies: "getHydraCompanies",
      deleteCompany: "deleteCompany",
      getCountry: "getCountry"
    }),

    // Функция для генерации пунктов контекстного меню - ТОЛЬКО EDIT!
    getCompanyMenuItems(actionData) {
      const items = [];

      // Пункт редактирования - только он, как в action-type="company"
      items.push({
        label: this.$t("counterparties.editAction"),
        command: () => {
          this.showCompanyManager(actionData.index);
        }
      });

      // НЕТ ПУНКТА DELETE! Он только в мобильной версии

      return [
        {
          items: items
        }
      ];
    },

    showCompanyManager(index) {
      // Если index === null, значит создаем новую компанию
      if (index === null) {
        this.$router.push({ name: "company manager", params: { id: null } });
        return;
      }

      // Проверяем, можно ли редактировать
      if (this.companies[index]?.isVerifyRequest) {
        this.showError(this.$t("toasts.error.companyEdit"));
        return;
      }

      this.$router.push({ name: "company manager", params: { id: this.companies[index].id } });
    },

    showDeleteDialog(index) {
      this.deleteId = this.companies[index].id;
      this.deleteName = this.companies[index].nameRus;
      this.deleteIndex = index;
      this.deleteDialog = true;
    },

    async del() {
      this.deleteDialog = false;
      try {
        await this.deleteCompany(this.deleteId);
        this.companies.splice(this.deleteIndex, 1);
        this.showSuccess(this.$t("toasts.success.companyDelete"));
      }
      catch (e) {
        this.showError(this.$t("toasts.error.companyDelete"));
      }
    },

    async getCompaniesData(page) {
      this.loading = true;
      try {
        await this.getHydraCompanies({
          page: page,
          itemsPerPage: this.itemsPerPage
        });
      }
      catch (e) {
        this.showError(e.response.data?.message || e.response.data?.detail);
      }
      finally {
        this.loading = false;
      }
    },

    showSuccess(message) {
      this.$toast.success(message);
    },

    showError(message) {
      this.$toast.error(message);
    }
  }
};
</script>

<style lang="scss" scoped>
.companies {
  &-info {
    padding: 20px;
    .title {
      font-size: 24px;
      color: var(--text-color);
      line-height: 1.1;
    }
    .form-header {
      margin-bottom: 20px;
    }
  }

  &-count {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-weight: 500;
    font-size: 15px;
    color: var(--text-color);
    margin-bottom: 40px;
    span {
      color: var(--color-60);
      font-weight: 500;
      font-size: 11px;
    }
  }

  &-table {
    @media screen and (max-width:500px) {
      display: none;
    }
  }

  &-accordion {
    display: none;

    @media screen and (max-width:500px) {
      display: block;
    }

    .buttons {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
  }
}

// Добавляем класс для скрытия таблицы на мобильных устройствах
.briefcase-table {
  &-hiddable {
    @media screen and (max-width:500px) {
      display: none;
    }
  }
}

.col {
  padding: 0;
  font-size: 12px;
  &:not(.info-title) {
    text-align: center;
  }
}

.company {
  &-header {
    color: var(--text-color);
    font-size: 12px;
  }
  &-info {
    color: var(--text-color);
    display: flex;
    flex-direction: column;
    gap: 20px
  }
}
.modal_head_text {
  font-weight: 450;
  font-size: 20px;
}
.btn_indent{
  display: flex;
  justify-content: end;
}
.modal_button {
  display: flex;
  justify-content: end;
  margin-top: 15px;
}
.modal_button_margin {
  margin-right: 10px;
}
.btn-text{
  font-weight: 450;
  font-size: 12px;
}
.companies-table {
  margin-bottom: 0;
}
.items-count {
  display: flex;
  align-items: center;
  gap: 5px;

  span {
    font-weight: 450;
    font-size: 12px;
    color: var(--color-60);
  }
}
</style>